/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
import { getShortDateTimeString } from '@/services/utils';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useInvoiceState } from '@/modules/billing/review/editDraft/useInvoiceState';
export const useInvoiceEditorStore = defineStore('invoiceEditorStore', {
  state: () => {
    const {
      handleError
    } = useSnackbarHandler();
    const {
      updateLineItem
    } = useInvoiceState();
    return {
      handleError,
      updateLineItem,
      accountingClientId: '',
      accountingClient: {},
      accountingAccountId: '',
      accountingAccount: {},
      isSaving: false,
      selectedLineItem: {},
      selectedGroup: {},
      showEditCreditLineItemModal: false,
      showEditAdminLineItemModal: false,
      showEditEntryLineItemModal: false,
      showEditExpenseLineItemModal: false,
      showEditFixedFeeLineItemModal: false,
      showNewTimeEntryModal: false,
      showNewExpenseModal: false,
      showNewFixedFeeModal: false,
      showMatterChangeModal: false,
      onlyViewInvoice: false,
      // PDF preview state
      isGeneratingPdf: false,
      isPollingPdf: false,
      pdfUrl: '',
      pdfError: null
    };
  },
  actions: {
    async getAccountingClient(accountingClientId) {
      try {
        if (this.accountingClientId === accountingClientId) {
          return this.accountingClient;
        }
        const response = await axios.get(`/accounting/clients/${accountingClientId}`);
        this.$patch({
          accountingClient: response.data
        });
        return response.data;
      } catch (err) {
        this.handleError(err);
        return {};
      }
    },
    async getAccountingAccount(accountingAccountId) {
      try {
        if (this.accountingAccountId === accountingAccountId) {
          return this.accountingAccount;
        }
        const response = await axios.get(`/accounting/accounts/${accountingAccountId}`);
        this.$patch({
          accountingAccount: response.data
        });
        return response.data;
      } catch (err) {
        this.handleError(err);
        return {};
      }
    },
    async getInvoice({
      id
    }) {
      try {
        var response = await axios.get(`/invoices/${id}`);
        return response.data;
      } catch (error) {
        this.handleError(error);
      }
    },
    getInvoiceAccounts({
      id
    }) {
      return axios.get(`/invoices/${id}/accounts`);
    },
    getMatterBilling({
      id
    }) {
      return axios.get(`/matters/${id}/billing`);
    },
    getClientBilling({
      id
    }) {
      return axios.get(`/clients/${id}/billing`);
    },
    setInvoiceHeaderText(state) {
      if (state.draft) return 'Draft';
      if (state.review) return 'Review';
      if (state.approved) return 'Approved';
      if (state.open) return 'Open';
      if (state.closed) return 'Paid';
      if (state.deleted) return 'Deleted';
      return 'Invoice';
    },
    async draftInvoiceList({
      clientId,
      matterId,
      timeSheetId
    }) {
      try {
        let response = await axios.get(`invoices?state=draft,review&clientId=${clientId}&matterId=${matterId}&scope=all`);
        let data = response.data;
        const draftInvoices = data.invoices.map(invoice => ({
          timeSheetId: invoice.id,
          created: invoice.id === timeSheetId ? 'This Invoice' : `Created: ${getShortDateTimeString(invoice.created)} `,
          matter: invoice.matter,
          matters: invoice.matters
        }));
        return draftInvoices;
      } catch (err) {
        this.handleError(err);
        return [];
      }
    },
    async updateInvoice({
      id,
      data
    }) {
      try {
        var response = await axios.put(`/invoices/${id}`, data);
        return response.data;
      } catch (error) {
        this.handleError(error);
      }
    },
    async recallInvoice({
      id
    }) {
      try {
        await axios.post(`/invoices/${id}/recall`, {});
      } catch (err) {
        this.handleError(err);
        return {};
      }
    },
    async processInvoice({
      id,
      data
    }) {
      try {
        await axios.post(`/invoices/${id}/process`, data);
      } catch (err) {
        this.handleError(err);
        return {};
      }
    },
    async deleteInvoice({
      id
    }) {
      try {
        await axios.delete(`/invoices/${id}`);
      } catch (err) {
        this.handleError(err);
      }
    },
    async fetchAssociatedSplitBillingInvoices({
      id
    }) {
      try {
        const response = await axios.get(`/invoices/${id}/split-recall-preview`);
        return response.data.associatedInvoices || [];
      } catch (err) {
        this.handleError(err);
      }
    },
    editLineItem(lineItem, changeMatter = false, bulk = false) {
      if (bulk && changeMatter) {
        this.selectedLineItem = {};
        this.showMatterChangeModal = true;
      } else {
        this.selectedLineItem = lineItem;
        if (changeMatter) {
          this.showMatterChangeModal = true;
        } else {
          this.showEditCreditLineItemModal = lineItem?.credit !== undefined;
          this.showEditAdminLineItemModal = lineItem?.computed !== undefined;
          this.showEditEntryLineItemModal = lineItem?.entries !== undefined && lineItem.entries;
          this.showEditEntryLineItemModal = lineItem?.entries !== undefined && lineItem.entries;
          this.showEditExpenseLineItemModal = lineItem?.expenses !== undefined;
          this.showEditFixedFeeLineItemModal = lineItem?.charges !== undefined && !lineItem.entries;
          this.showEditFixedFeeLineItemModal = lineItem?.charges !== undefined && !lineItem.entries;
        }
      }
    },
    cancelAddLineItem() {
      this.showNewExpenseModal = false;
      this.showNewFixedFeeModal = false;
      this.showNewTimeEntryModal = false;
    },
    newFixedFee(lineItem, group) {
      this.selectedLineItem = lineItem;
      this.selectedGroup = group;
      this.showNewFixedFeeModal = true;
    },
    async saveNewFixedFee(newFixedFee) {
      try {
        let response = await axios.post('/charges', newFixedFee);
        let fixedFee = response.data;
        this.selectedLineItem.description = fixedFee.description;
        this.selectedLineItem.amount = fixedFee.amount;
        this.selectedLineItem.rate = fixedFee.rate;
        this.selectedLineItem.codes = fixedFee.codes;
        this.selectedLineItem.account = fixedFee.account;
        this.selectedLineItem.date = fixedFee.date;
        this.selectedLineItem.charges.splice(0, 1, fixedFee);
        this.selectedLineItem.id = fixedFee.id;
        this.sortGroup(this.selectedGroup);
        this.showNewFixedFeeModal = false;
        this.selectedGroup.lineItems.push(this.selectedLineItem);
        this.sortGroup(this.selectedGroup);
        this.updateLineItem(newFixedFee);
      } catch (error) {
        this.handleError(error);
      }
    },
    newTimeEntry(lineItem, group) {
      this.selectedLineItem = lineItem;
      this.selectedGroup = group;
      this.showNewTimeEntryModal = true;
    },
    async saveNewEntry(newEntry) {
      newEntry.billedHours = newEntry.hours;
      newEntry.billingType = newEntry.billingType.id;
      newEntry.startTime = new Date(newEntry.startTime);
      newEntry.date = new Date(newEntry.date);
      newEntry.entrySource = 'Draft';
      try {
        let response = await axios.post('/entries', newEntry);
        let entry = response.data;
        this.selectedLineItem.hours = entry.hours;
        this.selectedLineItem.description = entry.description;
        this.selectedLineItem.rate = entry.billingType === 'billable' ? {
          amount: entry.rate.amount
        } : {
          amount: 0
        };
        this.selectedLineItem.account = entry.account;
        this.selectedLineItem.codes = entry.codes || {
          task: {
            id: null
          },
          activity: {
            id: null
          }
        };
        this.selectedLineItem.customFlag = entry.customFlag;
        this.selectedLineItem.date = entry.date;
        this.selectedLineItem.amount = entry.billingType === 'billable' ? entry.hours * entry.rate.amount : 0;
        this.selectedLineItem.entries.splice(0, 1, entry);
        this.selectedLineItem.id = entry.id;
        this.selectedGroup.lineItems.push(this.selectedLineItem);
        this.sortGroup(this.selectedGroup);
        this.showNewTimeEntryModal = false;
        this.updateLineItem(this.selectedLineItem);
      } catch (error) {
        this.handleError(error);
      }
    },
    sameDate(date1, date2) {
      const a = new Date(date1);
      const b = new Date(date2);
      return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    },
    sortGroup(group) {
      const meStore = useMeStore();
      const descending = meStore.me.settings.invoice?.invoiceSortReverse || false;
      group.lineItems.sort((a, b) => {
        if (descending) {
          return new Date(b.date) - new Date(a.date);
        }
        return new Date(a.date) - new Date(b.date);
      });
    },
    newExpense(lineItem, group) {
      this.selectedLineItem = lineItem;
      this.selectedGroup = group;
      this.showNewExpenseModal = true;
    },
    async saveNewExpense(newExpenseLineItem, keepModalOpen = false) {
      try {
        let response = await axios.post('/expenses', newExpenseLineItem.expenses[0]);
        let expense = response.data;
        expense.matterId = expense.matter.id;
        expense.attachments = expense.attachments || [];
        this.selectedLineItem.amount = expense.amount;
        this.selectedLineItem.description = expense.description;
        this.selectedLineItem.account = expense.account;
        this.selectedLineItem.codes = {
          task: expense.code
        };
        this.selectedLineItem.customFlag = expense.customFlag;
        this.selectedLineItem.matterId = expense.matter?.id;
        this.selectedLineItem.date = expense.date;
        this.selectedLineItem.billed = expense.billed;
        this.selectedLineItem.expenses[0] = {
          ...expense
        };
        this.selectedLineItem.id = expense.id;
        this.selectedGroup.lineItems.push({
          ...this.selectedLineItem
        });
        this.sortGroup(this.selectedGroup);
        const invoiceState = useInvoiceState();
        invoiceState.updateLineItem({
          ...this.selectedLineItem
        });
        if (!keepModalOpen) {
          this.showNewExpenseModal = false;
        }
        return expense;
      } catch (error) {
        this.handleError(error);
        return undefined;
      }
    },
    setViewInvoice(invoiceState, permissions) {
      this.onlyViewInvoice = !(invoiceState === 'draft' || invoiceState === 'review') || !permissions.value.billing?.edit;
    },
    // PDF Preview methods
    async generateDraftPdf(invoiceId) {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      try {
        this.isGeneratingPdf = true;
        this.pdfError = null;

        // POST request to queue PDF generation
        await axios.post(`/Invoices/${invoiceId}/preview-draft-pdf`);
        return true;
      } catch (err) {
        this.pdfError = err;
        this.handleError(err);
        throw err;
      } finally {
        this.isGeneratingPdf = false;
      }
    },
    async checkDraftPdfStatus(invoiceId) {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      try {
        // GET request to check PDF generation status
        const response = await axios.get(`/Invoices/${invoiceId}/draft-pdf-status`);
        return response.data;
      } catch (err) {
        this.pdfError = err;
        this.handleError(err);
        throw err;
      }
    },
    async pollForPdfReady(invoiceId, maxAttempts = 30, initialDelay = 1000) {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      let attempts = 0;
      let delay = initialDelay;
      this.isPollingPdf = true;
      this.pdfError = null;
      try {
        while (attempts < maxAttempts) {
          attempts++;
          const status = await this.checkDraftPdfStatus(invoiceId);
          if (status?.isPdfReady) {
            this.pdfUrl = status.url || '';
            return status;
          }

          // Wait before next attempt with exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
          delay = Math.min(delay * 1.2, 2000); // Cap at 2 seconds
        }
        throw new Error('PDF generation timed out. Please try again.');
      } catch (err) {
        this.pdfError = err;
        this.handleError(err);
        throw err;
      } finally {
        this.isPollingPdf = false;
      }
    },
    async downloadInvoicePreview(invoiceId = null) {
      try {
        // If no invoiceId provided, get current invoice ID from state
        if (!invoiceId) {
          const invoiceState = useInvoiceState();
          invoiceId = invoiceState.id;
          if (!invoiceId) {
            throw new Error('No current invoice available for preview');
          }
        }

        // Use the same flow for both regular and template preview
        // First, generate the PDF
        await this.generateDraftPdf(invoiceId);

        // Then poll until it's ready
        const status = await this.pollForPdfReady(invoiceId);
        if (status?.url) {
          // Construct full URL from relative path using the domain from config
          const fullUrl = status.url.startsWith('http') ? status.url : `${window.leanlaw.domain}${status.url}`;

          // Open PDF in new tab with full URL
          window.open(fullUrl, '_blank');
          return status;
        } else {
          throw new Error('PDF URL not available');
        }
      } catch (err) {
        this.handleError('Failed to generate invoice preview. Please try again.');
        throw err;
      }
    },
    resetPdfState() {
      this.isGeneratingPdf = false;
      this.isPollingPdf = false;
      this.pdfUrl = '';
      this.pdfError = null;
    },
    resetModalStates() {
      this.showEditCreditLineItemModal = false;
      this.showEditAdminLineItemModal = false;
      this.showEditEntryLineItemModal = false;
      this.showEditExpenseLineItemModal = false;
      this.showEditFixedFeeLineItemModal = false;
      this.showNewTimeEntryModal = false;
      this.showNewExpenseModal = false;
      this.showNewFixedFeeModal = false;
      this.showMatterChangeModal = false;
      this.selectedLineItem = {};
      this.selectedGroup = {};
    }
  }
});